'use client';

import { useEffect } from 'react';

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface AppPlugin {
  addListener: (
    event: 'appRestoredResult',
    handler: (data: unknown) => void,
  ) => PluginListenerHandle | Promise<PluginListenerHandle>;
}

interface CapacitorGlobal {
  Plugins?: { App?: AppPlugin };
  isNativePlatform?: () => boolean;
}

/**
 * Handles Android activity restoration via Capacitor's `appRestoredResult` event.
 *
 * On Android, the OS may kill the app process while a plugin activity (like Camera)
 * is open to reclaim memory. When the user finishes the plugin action, the app restarts
 * and this event fires to deliver the plugin payload that would have normally been
 * returned as a promise resolution.
 *
 * We dispatch a custom window event so specific components (like avatar upload)
 * can hook in and consume the payload.
 */
export function CapacitorRestoredResultHandler() {
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor as CapacitorGlobal | undefined;

    if (!cap?.isNativePlatform?.()) return;

    const app = cap.Plugins?.App;
    if (!app) return;

    const handle: {
      cancelled: boolean;
      remove: (() => Promise<void> | void) | null;
    } = {
      cancelled: false,
      remove: null,
    };

    Promise.resolve(
      app.addListener('appRestoredResult', (data) => {
        // Dispatch to the DOM so other components can consume it without
        // needing direct Capacitor bindings.
        const event = new CustomEvent('capacitorAppRestoredResult', {
          detail: data,
        });
        window.dispatchEvent(event);
      }),
    )
      .then((listener) => {
        if (handle.cancelled) {
          listener.remove();
        } else {
          handle.remove = listener.remove;
        }
      })
      .catch(() => {
        // Ignore failure on older platforms
      });

    return () => {
      handle.cancelled = true;
      handle.remove?.();
    };
  }, []);

  return null;
}
